/**
 * @fileoverview Soil Mix Details component for displaying recommended soil mix
 * @module components/SoilMixDetails
 * @requires react
 */

import React, { useState } from 'react';
import './SoilMixDetails.css';

/**
 * SoilMixDetails component for displaying recommended soil mix
 * @component
 * @param {Object} props - Component properties
 * @param {Object} props.soilMix - Soil mix data
 * @param {Function} props.onAddToCart - Callback function when adding to cart
 * @returns {JSX.Element} SoilMixDetails component
 */
const SoilMixDetails = ({ soilMix, onAddToCart }) => {
  // State for quantity
  const [quantity, setQuantity] = useState(1);
  // State for added to cart status
  const [addedToCart, setAddedToCart] = useState(false);

  // If no soil mix is provided, return null
  if (!soilMix) {
    return null;
  }

  /**
   * Handle quantity change
   * @param {Event} e - Change event
   */
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      setQuantity(value);
    }
  };

  /**
   * Handle add to cart
   */
  const handleAddToCart = () => {
    if (onAddToCart) {
      // Pass the soilMix with quantity to the callback
      onAddToCart({ ...soilMix, quantity });
      
      // Set added to cart status
      setAddedToCart(true);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setAddedToCart(false);
      }, 3000);
    }
  };

  return (
    <div className="soil-mix-container">
      <div className="soil-mix-header">
        <div>
          <h2 className="soil-mix-title">{soilMix.name}</h2>
          <p className="soil-mix-plant">Perfect for {soilMix.forPlant}</p>
        </div>
        <div className="soil-mix-price">${soilMix.price.toFixed(2)}</div>
      </div>
      
      <p className="soil-mix-description">{soilMix.description}</p>
      
      <div className="soil-mix-components">
        <h3>Components</h3>
        <ul className="components-list">
          {soilMix.components.map((component, index) => (
            <li key={index} className="component-item">
              <span className="component-name">{component.name}</span>
              <span className="component-percentage">{component.percentage}%</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="soil-mix-benefits">
        <h3>Benefits</h3>
        <ul className="benefits-list">
          {soilMix.benefits.map((benefit, index) => (
            <li key={index}>{benefit}</li>
          ))}
        </ul>
      </div>
      
      <div className="soil-mix-specifications">
        <div className="specification">
          <h4>pH Level</h4>
          <p>{soilMix.phLevel}</p>
        </div>
        <div className="specification">
          <h4>Drainage</h4>
          <p>{soilMix.drainage}</p>
        </div>
        <div className="specification">
          <h4>Nutrient Retention</h4>
          <p>{soilMix.nutrientRetention}</p>
        </div>
        <div className="specification">
          <h4>Weight</h4>
          <p>{soilMix.weight} lbs</p>
        </div>
      </div>
      
      <div className="soil-mix-actions">
        <div className="quantity-selector">
          <label htmlFor="quantity">Quantity:</label>
          <input
            type="number"
            id="quantity"
            min="1"
            value={quantity}
            onChange={handleQuantityChange}
          />
        </div>
        
        <button
          className="add-to-cart-btn"
          onClick={handleAddToCart}
          disabled={addedToCart}
        >
          {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
};

export default SoilMixDetails;